import { getStroke } from "perfect-freehand";

type Point = [number, number, number];

function randomValue(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  };
}

function outlinePath(points: Point[], size: number): string {
  const stroke = getStroke(points, {
    size,
    thinning: 0.48,
    smoothing: 0.55,
    streamline: 0.38,
    easing: (value) => value * (2 - value),
    start: { taper: 0, cap: true },
    end: { taper: size * 0.35, cap: true },
  });
  if (stroke.length < 3) return "";

  const first = stroke[0];
  if (!first) return "";
  const commands = [`M ${first[0].toFixed(1)} ${first[1].toFixed(1)}`, "Q"];
  for (let index = 0; index < stroke.length; index += 1) {
    const point = stroke[index];
    const next = stroke[(index + 1) % stroke.length];
    if (!point || !next) continue;
    commands.push(
      `${point[0].toFixed(1)} ${point[1].toFixed(1)} ${((point[0] + next[0]) / 2).toFixed(1)} ${((point[1] + next[1]) / 2).toFixed(1)}`,
    );
  }
  commands.push("Z");
  return commands.join(" ");
}

function wobblyLine(
  from: [number, number],
  to: [number, number],
  seed: number,
  pointCount = 11,
): Point[] {
  const random = randomValue(seed);
  const deltaX = to[0] - from[0];
  const deltaY = to[1] - from[1];
  const length = Math.hypot(deltaX, deltaY) || 1;
  const normalX = -deltaY / length;
  const normalY = deltaX / length;
  const points: Point[] = [];

  for (let index = 0; index < pointCount; index += 1) {
    const progress = index / (pointCount - 1);
    const wave = Math.sin(progress * Math.PI * 2 + random() * 0.35) * 1.8;
    const drift = (random() - 0.5) * 2.4 + wave;
    points.push([
      from[0] + deltaX * progress + normalX * drift,
      from[1] + deltaY * progress + normalY * drift,
      0.68 + Math.sin(progress * Math.PI) * 0.24 + (random() - 0.5) * 0.08,
    ]);
  }
  return points;
}

function boxPaths(seed: number): string[] {
  const boxes = [365, 485, 605];
  return boxes.flatMap((x, boxIndex) => {
    const left = x - 8;
    const right = x + 90;
    const top = 235 + (boxIndex % 2) * 2;
    const bottom = 344 - (boxIndex % 2) * 2;
    return [
      outlinePath(wobblyLine([left - 4, top], [right + 3, top + 1], seed + boxIndex * 13), 6.8),
      outlinePath(wobblyLine([right, top - 3], [right + 1, bottom + 5], seed + boxIndex * 13 + 1), 7.1),
      outlinePath(wobblyLine([right + 4, bottom], [left - 3, bottom + 1], seed + boxIndex * 13 + 2), 6.5),
      outlinePath(wobblyLine([left, bottom + 3], [left - 1, top - 5], seed + boxIndex * 13 + 3), 7.3),
    ];
  });
}

function scribblePaths(seed: number): string[] {
  return [0, 1, 2, 3].map((line) => {
    const startY = 256 + line * 20;
    const endY = 318 - line * 15;
    return outlinePath(wobblyLine([340, startY], [725, endY], seed + line * 19, 18), 9.5);
  });
}

function circlePath(seed: number): string {
  const random = randomValue(seed + 913);
  const points: Point[] = [];
  const count = 52;
  for (let index = 0; index <= count; index += 1) {
    const angle = (index / count) * Math.PI * 2;
    const radiusX = 225 + (random() - 0.5) * 8;
    const radiusY = 84 + (random() - 0.5) * 5;
    points.push([
      535 + Math.cos(angle) * radiusX,
      291 + Math.sin(angle) * radiusY,
      0.66 + Math.sin(index * 0.7) * 0.12,
    ]);
  }
  return outlinePath(points, 8.5);
}

export function strokeLayer(
  seed: number,
  result: "none" | "wrong" | "solved" | "failed",
): string {
  const boxes = boxPaths(seed)
    .map((path) => `<path d="${path}" />`)
    .join("");
  const resultPaths =
    result === "wrong"
      ? `<g class="result-stroke scribble-stroke">${scribblePaths(seed)
          .map((path) => `<path d="${path}" />`)
          .join("")}</g>`
      : result === "solved"
        ? `<g class="result-stroke solve-stroke"><path d="${circlePath(seed)}" /></g>`
        : "";

  return `
    <svg class="stroke-layer" viewBox="0 0 1000 1250" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <filter id="rough-ink" x="-4%" y="-4%" width="108%" height="108%">
          <feTurbulence type="fractalNoise" baseFrequency="0.012 0.04" numOctaves="2" seed="${seed % 97}" result="roughness" />
          <feDisplacementMap in="SourceGraphic" in2="roughness" scale="1.25" xChannelSelector="R" yChannelSelector="G" />
        </filter>
      </defs>
      <g class="box-strokes" filter="url(#rough-ink)">${boxes}</g>
      ${resultPaths}
    </svg>`;
}